import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api';
import { KsefSettingsForm } from './KsefSettingsForm';

/**
 * `GET /api/ksef/status` (F33): never the token, only the mode, the NIP and whether the offline certificate is set;
 * where the settings come from (owner 2026-11-01: the app's settings, else the server's `.env`) and the error of an
 * app setting that cannot be used (e.g. a token the server can no longer read).
 */
export type KsefStatus = {
  mode: 'off' | 'test' | 'demo' | 'prod';
  nip: string;
  offlineCertificate: boolean;
  configError: boolean;
  queue: { oczekuje: number; wysylana: number; odrzucona: number };
  /** Waiting documents queued in another mode (e.g. test before the switch to prod): never sent in this one. */
  otherEnv: number;
  source: 'app' | 'env';
  settingsError: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  /** F41: the download of invoices from KSeF (every hour; "Pobierz teraz"). */
  incoming: { lastSyncAt: string | null; lastError: string | null; lastErrorAt: string | null; catchingUp: boolean; running: boolean };
};

export const KSEF_STATUS_RESOURCE = 'ksef.status';

const MODES: Record<KsefStatus['mode'], string> = {
  off: 'Wyłączony – faktury zostają robocze',
  test: 'Testowy (środowisko testowe MF, bez mocy prawnej)',
  demo: 'Przedprodukcyjny (środowisko DEMO MF, bez mocy prawnej)',
  prod: 'Produkcyjny',
};

const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const when = (iso: string) => WHEN.format(new Date(iso));
const nip = (n: string) => (/^\d{10}$/.test(n) ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 8)}-${n.slice(8)}` : n);
const rejected = (n: number) => `${n} ${n === 1 ? 'odrzucony' : 'odrzucone'}`;
/** "1 dokument dodany", "3 dokumenty dodane", "5 dokumentów dodanych". */
function documents(n: number): string {
  if (n === 1) return 'dokument dodany';
  const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return few ? 'dokumenty dodane' : 'dokumentów dodanych';
}
const SOURCES: Record<KsefStatus['source'], string> = { app: 'z ustawień aplikacji', env: 'z pliku serwera' };
const queueText = (q: KsefStatus['queue']) => `${q.oczekuje} czeka na wysłanie · ${q.wysylana} czeka na KSeF · ${rejected(q.odrzucona)}`;

/**
 * "Finanse → Ustawienia → KSeF": the mode of work and the state of the connection; the owner also gets the settings
 * form (environment, NIP, token — `KsefSettingsForm`).
 */
export function KsefStatusCard({ isOwner = false }: { isOwner?: boolean }) {
  const queryClient = useQueryClient();
  // while a download runs, the card asks every 2 s; the lists are refreshed when it ends
  const status = useQuery({ queryKey: ['ksef', 'status'], queryFn: () => api<KsefStatus>('/api/ksef/status'), refetchInterval: (q) => (q.state.data?.incoming.running ? 2_000 : 30_000) });
  const sync = useMutation({
    mutationFn: () => api<{ started: boolean }>('/api/ksef/incoming/sync', { method: 'POST' }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['ksef', 'status'] }),
  });
  const s = status.data;
  const running = sync.isPending || (s?.incoming.running ?? false);
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (running) setWasRunning(true);
    else if (wasRunning) {
      setWasRunning(false);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
    }
  }, [running, wasRunning, queryClient]);
  const lastSync = (i: KsefStatus['incoming']) => {
    if (i.catchingUp) return i.lastSyncAt ? 'trwa pobieranie zaległych faktur… (KSeF pozwala pobrać ok. 60 faktur na godzinę)' : 'trwa pierwsze pobieranie… (KSeF pozwala pobrać ok. 60 faktur na godzinę)';
    return i.lastSyncAt ? when(i.lastSyncAt) : 'jeszcze nie było';
  };
  return (
    <section aria-label="KSeF" className="ksef-status" data-feature-id="ksef.status">
      <h2>KSeF</h2>
      <p className="lede">Wysyłka faktur do Krajowego Systemu e-Faktur. Środowisko, NIP firmy i token ustawia właściciel poniżej; bez tego obowiązuje plik .env na serwerze (instrukcja wdrożenia, punkt 13).</p>
      {status.error && <p className="alert" role="alert">{status.error instanceof ApiError ? status.error.message : 'Nie udało się wczytać stanu KSeF'}</p>}
      {s?.configError && (
        <p className="alert" role="alert">
          Błąd konfiguracji: KSeF odrzucił token. Wygeneruj nowy token w Aplikacji Podatnika KSeF i zapisz go w ustawieniach KSeF (albo w .env na serwerze).
        </p>
      )}
      {s?.settingsError && (
        <p className="alert" role="alert">
          {s.settingsError}
        </p>
      )}
      {s && s.otherEnv > 0 && (
        <p className="notice" role="status">
          {`${s.otherEnv} ${documents(s.otherEnv)} w innym trybie KSeF (np. testowym) nie zostaną wysłane w tym trybie.`}
        </p>
      )}
      {s && (
        <dl className="data-list">
          <div className="data-row">
            <dt>Tryb</dt>
            <dd>{MODES[s.mode]}</dd>
          </div>
          <div className="data-row">
            <dt>Ustawienia</dt>
            <dd>{SOURCES[s.source]}</dd>
          </div>
          {s.mode !== 'off' && (
            <>
              <div className="data-row">
                <dt>NIP firmy</dt>
                <dd>{nip(s.nip)}</dd>
              </div>
              <div className="data-row">
                <dt>Kolejka</dt>
                <dd>{queueText(s.queue)}</dd>
              </div>
              <div className="data-row">
                <dt>Ostatnie połączenie</dt>
                <dd>{s.lastSuccessAt ? when(s.lastSuccessAt) : 'jeszcze nie było'}</dd>
              </div>
              {s.lastError && s.lastErrorAt && (
                <div className="data-row">
                  <dt>Ostatni błąd</dt>
                  <dd>{`${when(s.lastErrorAt)} — ${s.lastError}`}</dd>
                </div>
              )}
              <div className="data-row">
                <dt>Certyfikat offline</dt>
                <dd>{s.offlineCertificate ? 'jest' : 'brak'}</dd>
              </div>
              <div className="data-row" data-feature-id="ksef.incoming">
                <dt>Ostatnie pobranie faktur</dt>
                <dd>{lastSync(s.incoming)}</dd>
              </div>
              {s.incoming.lastError && s.incoming.lastErrorAt && (
                <div className="data-row">
                  <dt>Błąd pobierania</dt>
                  <dd>{`${when(s.incoming.lastErrorAt)} — ${s.incoming.lastError}`}</dd>
                </div>
              )}
            </>
          )}
        </dl>
      )}
      {s && s.mode !== 'off' && (
        <div className="ksef-download">
          <button className="btn" type="button" disabled={running} onClick={() => sync.mutate()}>
            Pobierz teraz
          </button>
          <span className="muted">Faktury zakupowe i wystawione poza programem pobierają się same co godzinę.</span>
          {running && <p role="status">Pobieranie faktur z KSeF…</p>}
          {sync.error && (
            <p className="alert" role="alert">
              {sync.error instanceof ApiError ? sync.error.message : 'Nie udało się pobrać faktur z KSeF'}
            </p>
          )}
        </div>
      )}
      {s && isOwner && <KsefSettingsForm currentMode={s.mode} />}
    </section>
  );
}
